import { useMemo, useEffect } from 'react';
import { toast, useNavigate, useQueryState, useMutation, useI18n } from '@ywwwtseng/react-kit';
import { ArrowRightLeft } from 'lucide-react';
import * as web3 from '@ywwwtseng/web3';
import * as schema from '@/db/schema';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import { useTonWalletKit } from '@ywwwtseng/ton-wallet-kit';
import { useBalance } from '@/hooks/useBalance';
import { SelectNetwork } from '@/components/common/SelectNetwork';
import { SelectToken } from '@/components/common/SelectToken';
import {
  type FormElement,
  Form,
  validator,
  useGlobalForm,
} from '@/lib/react-form';
import { useToken } from '@/hooks/useToken';
import { useConnectedGuard } from '@/hooks/useConnectedGuard';
import { useBlockedWhenServiceUpdated } from '@/hooks/useBlockedWhenServiceUpdated';
import { useThrottle } from '@/hooks/useThrottle';
import { useWallets } from '@/hooks/useWallets';
import * as utils from '@/utils';
import { NETWORK_NAMES } from '@/constants';

export default function DepositScreen({
  params,
}: {
  params: { token_id?: string };
}) {
  const { t } = useI18n();
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const wallets = useWallets();
  const walletKitConnect = useWalletKitConnect();
  const tonWalletKit = useTonWalletKit();
  const navigate = useNavigate();

  const form = useGlobalForm({
    name: 'deposit',
    values: {
      default: {
        network: params.token_id
          ? utils.token.parseTokenId(params.token_id).network
          : null,
        token_id: params.token_id ?? null,
        amount: '',
      },
    },
  });

  const network = form.values.network as string | null;
  const token = useToken(form.values.token_id as string | null);
  const balance = useBalance(token);



  const isSwitchingNetwork = useMemo(() => {
    if (network === 'bsc' || network === 'ethereum') {
      const networkId = walletKitConnect.getNetwork(network)?.id;
      return walletKitConnect.currentChainId !== networkId;
    }

    return false;
  }, [walletKitConnect.currentChainId, network]);

  const depositMutation = useMutation('tx:deposit');

  const depositCallback = useConnectedGuard(
    async () => {
      try {
        if (!network) {
          return;
        }

        const source = wallets.connected[network as keyof typeof wallets.connected];
        const destination = wallets.offical[network as keyof typeof wallets.offical]?.address;

        if (!source) {
          toast.error(t('message.source_not_set'));
          return;
        }

        if (!destination) {
          toast.error(t('message.destination_not_set'));
          return;
        }

        if (!token) {
          return;
        }

        await wallets.sync(network);

        if (network === 'ton') {
          const tx_hash = await tonWalletKit.sendTransaction({
            token,
            destination,
            amount: web3.utils
              .parseUnits(form.values.amount as string, token.decimals)
              .toString(),
          });

          const result = await depositMutation.mutate({
            tx_hash,
            network,
            source,
            language_code: me?.language_code ?? 'en',
          });

          if (result.ok) {
            form.reset();
            navigate(-1);
          }
        } else {
          if (network === 'bsc' || network === 'ethereum') {
            const networkId = walletKitConnect.getNetwork(network)?.id;
            if (walletKitConnect.currentChainId !== networkId) {
              await walletKitConnect.disconnect(true);
              toast.error(t('message.network_not_switched'));
              return;
            }
          }

          const tx_hash = await walletKitConnect.sendTransaction({
            feePayer: source,
            source,
            token,
            destination,
            amount: web3.utils.parseUnits(
              form.values.amount as string,
              token.decimals
            ),
          });

          const result = await depositMutation.mutate({
            tx_hash,
            network,
            source,
            language_code: me?.language_code ?? 'en',
          });

          if (result.ok) {
            form.reset();
            navigate(-1);
          }
        }
      } catch (error) {
        toast.error((error as Error).message ?? t('message.deposit_failed'));
      }
    },
    {
      network: token?.network,
    }
  );

  const { mutate, isLoading } = useBlockedWhenServiceUpdated(depositCallback);

  const [deposit, isThrottled] = useThrottle(
    mutate,
    5000,
  );

  const elements = useMemo<{ row: FormElement[] }[]>(() => {
    const values = form.values;

    return [
      {
        row: [
          {
            ui: 'custom',
            name: 'network',
            validate: validator.required({
              message: t('validation.required', {
                name: t('common.network'),
              }),
            }),
            render: ({ setValue }) => (
              <SelectNetwork
                needConnected
                network={values.network as string | null}
                onNetworkSelected={(network) => {
                  setValue('network', network);
                }}
              />
            ),
          },
        ],
      },
      {
        row: [
          {
            ui: 'custom',
            name: 'token_id',

            dependencies: ['network'],

            validate: validator.required({
              message: t('validation.required', {
                name: t('common.token'),
              }),
            }),
            render: ({ values, setValue }) => {
              return (
                <SelectToken
                  hideBalance
                  disabled={!values.network}
                  network={values.network as string}
                  canChangeNetwork={false}
                  canAddToken={false}
                  value={values.token_id as string | null}
                  onChange={(value) => {
                    setValue('token_id', value);
                  }}
                />
              );
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'amount',
            label: t('deposit.amount'),
            dependencies: ['network', 'token'],
            name: 'amount',
            validate: [
              validator.gt(0, {
                message: t('validation.gt', {
                  name: t('deposit.amount'),
                  value: 0,
                }),
              }),
              (value: unknown) => {
                if (
                  value &&
                  token &&
                  utils.token.isInsufficient({
                    balance: balance.value,
                    amount: value as string,
                    decimals: token.decimals,
                  })
                ) {
                  return t('common.insufficient_balance');
                }
              },
            ],
            validateType: 'onChange',
            description: token
              ? t('common.wallet_balance', {
                balance: balance.formatted,
              })
              : null,
            props: {
              disabled: !values.token_id,
              placeholder: '0',
              decimal: token?.decimals ?? 0,
              className:
                values.amount &&
                  token &&
                  utils.token.isInsufficient({
                    balance: balance.value,
                    amount: values.amount as string,
                    decimals: token.decimals,
                  })
                  ? 'text-destructive'
                  : '',
            },
          },
        ],
      },
    ];
  }, [token, form.values, balance]);

  useEffect(() => {
    return () => {
      form.reset();
    };
  }, []);

  useEffect(() => {
    if (network) {
      try {
        void walletKitConnect.switchNetwork(network);
        toast(() => (
          <div className="flex items-center justify-center gap-4">
            <ArrowRightLeft size={20} strokeWidth={1.5} />
            <span>Switched to {NETWORK_NAMES[network as keyof typeof NETWORK_NAMES] ?? network}</span>
          </div>
        )
        );
      } catch (error) {
        toast.error((error as Error).message ?? t('message.switch_network_failed'));
      }
    }
  }, [network]);

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      <Form
        name="deposit"
        isLoading={isLoading || isThrottled || isSwitchingNetwork}
        {...form}
        elements={elements}
        submit={{
          label: t('common.deposit'),
        }}
        onSubmit={deposit}
      />
    </div>
  );
}
